'use client'

import {useEffect, useState} from 'react'
import {Download, Loader, X} from 'lucide-react'

type ImagePreviewOverlayProps = {
    /**
     * Primary URL rendered in the overlay. When `hdSrc` is also provided,
     * this value is used as a placeholder aspect-ratio hint — it is NOT
     * painted unless HD loading fails.
     */
    src: string
    /**
     * HD/original URL. When present the overlay immediately tries to load
     * this image. If the fetch fails, we fall back to `src` so the user
     * still sees something.
     */
    hdSrc?: string
    /** Used as the download filename; defaults to a generic one. */
    downloadName?: string
    /**
     * Fired once the HD image has been loaded and decoded in-browser.
     * Callers use this to flip the conversation bubble to the HD URL so
     * it stays on the original after the overlay closes.
     */
    onHdLoaded?: () => void
    onClose: () => void
}

export function ImagePreviewOverlay({src, hdSrc, downloadName, onHdLoaded, onClose}: ImagePreviewOverlayProps) {
    // When HD is available, display it directly. Fall back to the thumb URL
    // only if HD fails to load.
    const primarySrc = hdSrc || src
    const [currentSrc, setCurrentSrc] = useState(primarySrc)
    const [loading, setLoading] = useState(Boolean(hdSrc))

    useEffect(() => {
        function onKey(event: KeyboardEvent) {
            if (event.key === 'Escape') onClose()
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [onClose])

    // Reset on prop change (opening a different image).
    useEffect(() => {
        setCurrentSrc(primarySrc)
        setLoading(Boolean(hdSrc))
    }, [primarySrc, hdSrc])

    const downloadUrl = hdSrc || src

    return (
        <div
            className="fixed inset-0 z-preview bg-black/90 flex items-center justify-center cursor-zoom-out"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
        >
            <img
                src={currentSrc}
                alt="preview"
                className="max-w-[90%] max-h-[90%] object-contain cursor-default"
                onClick={(event) => event.stopPropagation()}
                onLoad={() => {
                    setLoading(false)
                    // Only notify when the HD URL was the one being painted —
                    // a fallback-to-thumb load should NOT mark the message as
                    // HD-cached.
                    if (hdSrc && currentSrc === hdSrc) {
                        onHdLoaded?.()
                    }
                }}
                onError={() => {
                    // HD failed: fall back to the thumb so the user still
                    // sees something rather than a broken-image icon.
                    if (hdSrc && currentSrc === hdSrc && src && src !== hdSrc) {
                        setCurrentSrc(src)
                    }
                    setLoading(false)
                }}
            />

            {loading ? (
                <div
                    className="absolute top-6 left-1/2 -translate-x-1/2 flex items-center gap-2 px-3 py-1.5 rounded bg-black/50 text-white text-xs"
                    onClick={(event) => event.stopPropagation()}
                >
                    <Loader size={14} className="animate-spin"/>
                    正在加载原图
                </div>
            ) : null}

            <div
                className="absolute top-6 right-6 flex items-center gap-3"
                onClick={(event) => event.stopPropagation()}
            >
                <a
                    href={downloadUrl}
                    download={downloadName || 'image'}
                    className="text-white/80 hover:text-white flex items-center gap-1.5 text-sm"
                    title="下载原图"
                >
                    <Download size={22}/>
                </a>
                <button
                    type="button"
                    className="text-white/80 hover:text-white"
                    onClick={onClose}
                    aria-label="关闭预览"
                >
                    <X size={28}/>
                </button>
            </div>
        </div>
    )
}
